import {
  RestaurantCustomer,
  CreateRestaurantCustomerData,
  UpdateRestaurantCustomerData,
  RestaurantCustomerFilters,
  RestaurantCustomerAddress as CustomerAddress,
  CreateCustomerAddressData,
  Order,
  Reservation,
} from "@lemmonhq/types";
import { getAPIClient } from "../helpers";

export type CustomersListResponse = {
  data: RestaurantCustomer[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    total_pages?: number;
  };
};

export const customersAPI = {
  getCustomers: async (
    filters?: RestaurantCustomerFilters,
  ): Promise<CustomersListResponse> => {
    const hc = getAPIClient();

    const query: Record<string, unknown> = {};

    if (filters?.search) {
      query.search = filters.search;
    }
    if (filters?.status_level) {
      query.status_level = filters.status_level;
    }
    if (filters?.tags?.length) {
      query.tags = filters.tags;
    }
    if (typeof filters?.limit === "number") {
      query.limit = String(filters.limit);
    }
    if (typeof filters?.offset === "number") {
      query.offset = String(filters.offset);
    }

    const resp = await hc.api.v2.customers.$get({
      // Hono client accepts string|string[] map; numbers converted to strings above
      query: query as Record<string, string | string[]>,
    });

    if (resp.ok) {
      const json: any = await resp.json();
      const rawData = json?.data;

      const data: RestaurantCustomer[] = Array.isArray(rawData)
        ? rawData
        : Array.isArray(rawData?.data)
          ? rawData.data
          : [];

      const paginationSource =
        (rawData && typeof rawData === "object" && rawData.pagination) ||
        json?.pagination ||
        {};

      const limitFallback =
        typeof filters?.limit === "number" ? filters.limit : 20;
      const offsetFallback =
        typeof filters?.offset === "number" ? filters.offset : 0;

      return {
        data,
        pagination: {
          total:
            typeof paginationSource.total === "number"
              ? paginationSource.total
              : data.length,
          limit:
            typeof paginationSource.limit === "number"
              ? paginationSource.limit
              : limitFallback,
          offset:
            typeof paginationSource.offset === "number"
              ? paginationSource.offset
              : offsetFallback,
          ...(paginationSource.total_pages !== undefined
            ? { total_pages: Number(paginationSource.total_pages) }
            : {}),
        },
      };
    }
    throw new Error("Failed to fetch customers");
  },

  getCustomer: async (customerId: string): Promise<RestaurantCustomer> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.customers[":id"].$get({
      param: { id: customerId },
    });

    if (resp.ok) {
      const json: any = await resp.json();
      if (json?.data && !Array.isArray(json.data)) {
        return json.data as RestaurantCustomer;
      }
      if (Array.isArray(json?.data)) {
        return json.data[0] as RestaurantCustomer;
      }
      if (Array.isArray(json)) {
        return json[0] as RestaurantCustomer;
      }
      return json as RestaurantCustomer;
    }
    throw new Error("Failed to fetch customer");
  },

  createCustomer: async (
    data: CreateRestaurantCustomerData,
  ): Promise<RestaurantCustomer> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.customers.$post({
      json: data,
    });

    if (resp.ok) {
      const json: any = await resp.json();
      if (Array.isArray(json?.data)) {
        return json.data[0] as RestaurantCustomer;
      }
      if (json?.data) {
        return json.data as RestaurantCustomer;
      }
      return json as RestaurantCustomer;
    }
    throw new Error("Failed to create customer");
  },

  getCustomerOrders: async (customerId: string): Promise<Order[]> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.customers[":id"].orders.$get({
      param: { id: customerId },
    });

    if (resp.ok) {
      const json: any = await resp.json();
      if (Array.isArray(json)) return json as Order[];
      if (Array.isArray(json?.data)) return json.data as Order[];
      return [];
    }
    throw new Error("Failed to fetch customer orders");
  },

  getCustomerReservations: async (
    customerId: string,
  ): Promise<Reservation[]> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.customers[":id"].reservations.$get({
      param: { id: customerId },
    });

    if (resp.ok) {
      const json: any = await resp.json();
      if (Array.isArray(json)) return json as Reservation[];
      if (Array.isArray(json?.data)) return json.data as Reservation[];
      return [];
    }
    throw new Error("Failed to fetch customer reservations");
  },

  updateCustomer: async (
    customerId: string,
    data: UpdateRestaurantCustomerData,
  ): Promise<RestaurantCustomer> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.customers[":id"].$patch({
      param: { id: customerId },
      json: data,
    });

    if (resp.ok) {
      const json: any = await resp.json();
      if (Array.isArray(json?.data)) {
        return json.data[0] as RestaurantCustomer;
      }
      if (json?.data) {
        return json.data as RestaurantCustomer;
      }
      return json as RestaurantCustomer;
    }
    throw new Error("Failed to update customer");
  },

  deleteCustomer: async (customerId: string): Promise<{ success: boolean }> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.customers[":id"].$delete({
      param: { id: customerId },
    });

    if (resp.ok) {
      return (await resp.json()) as { success: boolean };
    }
    throw new Error("Failed to delete customer");
  },

  getCustomerTags: async (): Promise<string[]> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.customers.tags.$get();

    if (resp.ok) {
      const json: any = await resp.json();
      if (Array.isArray(json)) {
        return json;
      }
      if (Array.isArray(json?.data)) {
        return json.data;
      }
      if (Array.isArray(json?.tags)) {
        return json.tags;
      }
      return [];
    }
    throw new Error("Failed to fetch customer tags");
  },

  getCustomerAddresses: async (
    customerId: string,
  ): Promise<CustomerAddress[]> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.customers[":id"].addresses.$get({
      param: { id: customerId },
    });

    if (resp.status === 404) {
      return [];
    }

    if (resp.ok) {
      const json: any = await resp.json();
      if (Array.isArray(json)) {
        return json;
      }
      if (Array.isArray(json?.data?.data)) {
        return json.data.data;
      }
      if (Array.isArray(json?.data)) {
        return json.data;
      }
      if (Array.isArray(json?.addresses)) {
        return json.addresses;
      }
      return [];
    }
    throw new Error("Failed to fetch customer addresses");
  },

  createCustomerAddress: async (
    customerId: string,
    data: CreateCustomerAddressData,
  ): Promise<CustomerAddress> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.customers[":id"].addresses.$post({
      param: { id: customerId },
      json: data,
    });

    if (resp.ok) {
      const json: any = await resp.json();
      if (json && typeof json === "object") {
        if (json.data && !Array.isArray(json.data)) {
          return json.data as CustomerAddress;
        }
        if (json.id) {
          return json as CustomerAddress;
        }
      }
      return json as CustomerAddress;
    }
    throw new Error("Failed to create customer address");
  },
};
